/**
 * "Connect a system" (ADR-0061): the connector host's discovery, credentials, test and build
 * API. Mirrors platform/services/tools/connectorhost/src/ap_tool_connectorhost/models.py.
 * Secret values go to the backend (OpenBao) and are never returned.
 */
import type { Http } from "./http";

const enc = encodeURIComponent;

export type ConnectType = "rest" | "soap" | "database" | "mcp";
export const CONNECT_TYPES: readonly ConnectType[] = ["rest", "soap", "database", "mcp"];
export type ActionKind = "read" | "change";
export type AuthKind = "none" | "api_key" | "bearer" | "oauth2_client_credentials" | "basic" | "wsse" | "database";
export const AUTH_KINDS: readonly AuthKind[] = ["none", "api_key", "bearer", "oauth2_client_credentials", "basic", "wsse", "database"];
export type McpTransport = "streamable_http" | "sse" | "stdio";
export type ConnectClassification = "public" | "internal" | "confidential" | "restricted";

/** Logical secret names per sign-in kind (same as SECRET_FIELDS in models.py). */
export const SECRET_FIELDS: Record<AuthKind, readonly string[]> = {
  none: [],
  api_key: ["api_key"],
  bearer: ["token"],
  oauth2_client_credentials: ["client_id", "client_secret"],
  basic: ["username", "password"],
  wsse: ["soap_username", "soap_password"],
  database: ["username", "password"],
};

export interface ConnectText {
  en: string;
  ar: string;
}

export interface Problem {
  code: string;
  /** Warnings are shown but never block Next or Save. */
  severity?: "error" | "warning";
  message: string;
  action?: string | null;
  field?: string | null;
  params?: Record<string, string>;
}

export interface KindSuggestion {
  value: ActionKind;
  reason: ConnectText;
}
export interface UndoSuggestion {
  action: string;
  args: Record<string, string>;
  reason: ConnectText;
}
export interface PiiSuggestion {
  field: string;
  reason: ConnectText;
}
export interface RolesSuggestion {
  value: string[];
  reason: ConnectText;
}
export interface FlagSuggestion {
  value: boolean;
  reason: ConnectText;
}
export interface Suggestions {
  kind: KindSuggestion;
  undo?: UndoSuggestion | null;
  pii: PiiSuggestion[];
  approver_roles?: RolesSuggestion | null;
  always_approve?: FlagSuggestion | null;
}

export interface BuiltinUndo {
  description: ConnectText;
  binding: Record<string, unknown>;
}
export interface UndoChoice {
  action?: string | null;
  args: Record<string, string>;
  builtin?: BuiltinUndo | null;
}

export interface DraftAction {
  name: string;
  source_name: string;
  include: boolean;
  kind: ActionKind;
  kind_locked: boolean;
  technical: string;
  description: ConnectText;
  ai_drafted: boolean;
  input_schema: Record<string, unknown>;
  output_schema: Record<string, unknown>;
  binding: Record<string, unknown>;
  fields: string[];
  undo?: UndoChoice | null;
  no_undo_reason: string;
  pii_fields: string[];
  approver_roles: string[];
  required_roles: string[];
  always_approve: boolean;
  suggestions: Suggestions;
}

export interface AuthConfig {
  kind: AuthKind;
  location: "header" | "query";
  param: string;
  token_url: string;
  scope: string;
  reason?: ConnectText | null;
}

export interface SoapPort {
  service: string;
  port: string;
  binding: string;
  soap_version: "1.1" | "1.2";
  address: string;
}

export interface DbConnection {
  engine: "postgres";
  host: string;
  port: number;
  database: string;
  schemas: string[];
  sslmode?: "disable" | "prefer" | "require" | "verify-full";
}

export interface McpConfig {
  transport: McpTransport;
  url: string;
  image: string;
  command: string[];
  server_name: string;
  server_version: string;
}

export interface SourceRef {
  kind: "url" | "file" | "registered_tool" | "database" | "approved_server";
  ref: string;
  digest: string;
}

export interface Draft {
  type: ConnectType;
  name: string;
  version: string;
  owner: string;
  title: string;
  description: ConnectText;
  base_url: string;
  egress_hosts: string[];
  output_classification: ConnectClassification;
  third_party: boolean;
  auth: AuthConfig;
  auth_options: AuthConfig[];
  soap_ports: SoapPort[];
  soap_port: string;
  database?: DbConnection | null;
  mcp?: McpConfig | null;
  source?: SourceRef | null;
  actions: DraftAction[];
  problems: Problem[];
}

export interface SourceIn {
  url?: string;
  content?: string;
  filename?: string;
  registered_tool?: string;
}

export interface CredentialsIn {
  kind: AuthKind;
  values: Record<string, string>;
  database?: DbConnection | null;
}

export interface DiscoverIn {
  type: ConnectType;
  source: SourceIn;
  name?: string;
  base_url?: string;
  soap_port?: string;
  database?: DbConnection | null;
  include_writes?: boolean;
  tables?: string[];
  mcp_transport?: McpTransport;
  /** Local (stdio) MCP: an approved server's name (ADR-0075). */
  mcp_server?: string;
  credentials?: CredentialsIn | null;
  describe_with_ai?: boolean;
}

export interface ValidateOut {
  ok: boolean;
  problems: Problem[];
}

export interface CredentialsStatus {
  tool: string;
  stored: boolean;
  kind?: AuthKind | null;
  fields: string[];
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface TestIn {
  draft: Draft;
  action: string;
  args: Record<string, unknown>;
}

export interface TestOut {
  action: string;
  mode: "executed" | "dry_run";
  ok: boolean;
  output?: Record<string, unknown> | null;
  planned?: Record<string, unknown> | null;
  redacted_fields: string[];
  audit_event_id?: string | null;
  error?: { code: string; message: string; retryable: boolean } | null;
}

export type DiffChange = "kind_to_change" | "kind_to_read" | "undo" | "pii" | "approvers" | "inputs" | "outputs" | "description";
export interface ActionChange {
  action: string;
  changes: (DiffChange | string)[];
}
export interface Diff {
  previous_version: string;
  added: string[];
  removed: string[];
  changed: ActionChange[];
}

export interface BuildOut {
  name: string;
  version: string;
  endpoint: string;
  registry_document: Record<string, unknown>;
  diff?: Diff | null;
  suggested_version?: string | null;
}

export interface HostedStatus {
  name: string;
  state: "loaded" | "error" | "not_loaded";
  version?: string | null;
  type?: ConnectType | null;
  endpoint: string;
  error?: string | null;
  policy?: { in_bundle: boolean; version?: string | null } | null;
}

export interface ReloadOut {
  changed: boolean;
  tools: HostedStatus[];
}

/** 422 body of discover/build: `{detail, code, params}` or `{detail, problems}`. */
export interface ConnectErrorBody {
  detail?: unknown;
  code?: string;
  params?: Record<string, string>;
  problems?: Problem[];
}

export function connectApi(http: Http) {
  return {
    discover: (body: DiscoverIn) => http.post<Draft>("connectorHost", "/v1/connect/discover", body),
    /** Approved local MCP servers (ADR-0075). */
    mcpServers: () => http.get<{ name: string; title: string; network: "none" | "egress" }[]>("connectorHost", "/v1/connect/mcp-servers"),
    validate: (draft: Draft) => http.post<ValidateOut>("connectorHost", "/v1/connect/validate", draft),
    credentials: (tool: string) => http.get<CredentialsStatus>("connectorHost", `/v1/connect/credentials/${enc(tool)}`),
    storeCredentials: (tool: string, body: CredentialsIn) => http.put<CredentialsStatus>("connectorHost", `/v1/connect/credentials/${enc(tool)}`, body),
    test: (body: TestIn) => http.post<TestOut>("connectorHost", "/v1/connect/test", body),
    build: (draft: Draft) => http.post<BuildOut>("connectorHost", "/v1/connect/build", draft),
    status: (tool: string) => http.get<HostedStatus>("connectorHost", `/v1/connect/status/${enc(tool)}`),
    hosted: () => http.get<HostedStatus[]>("connectorHost", "/v1/connect/hosted"),
    reload: () => http.post<ReloadOut>("connectorHost", "/v1/connect/reload", {}),
  };
}

export type ConnectApi = ReturnType<typeof connectApi>;

// ---- shared rules (the backend checks the same and more) ------------------------------------

export const TOOL_NAME = /^[a-z][a-z0-9-]{1,62}$/;
export const ACTION_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
export const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?$/;

/** Codes `draftProblems` checks live in the browser; the server's copies are superseded. */
export const CLIENT_CHECKED = new Set([
  "name_invalid", "version_invalid", "description_missing", "no_actions", "action_name_invalid", "action_name_duplicate",
  "read_cannot_change", "undo_target_self", "undo_target_missing", "undo_args_missing", "undo_or_reason_required",
]);

/** Live client problems plus the server's other findings (network, policy, sign-in). */
export function mergeProblems(local: readonly Problem[], server: readonly Problem[]): Problem[] {
  return [...local, ...server.filter((p) => !CLIENT_CHECKED.has(p.code))];
}

/** Is this action's undo complete (another action or a built-in one)? */
export function hasUndo(a: DraftAction): boolean {
  return !!a.undo && (!!a.undo.builtin || !!a.undo.action);
}

/**
 * The main safety rule in plain words (ADR-0029): every included action that changes data
 * needs a way to undo it or a reason why it cannot be undone. Also name/description checks.
 * Mirrors the backend's `validate`; the backend adds network and policy checks.
 */
export function draftProblems(d: Draft): Problem[] {
  const out: Problem[] = [];
  if (!TOOL_NAME.test(d.name)) out.push({ code: "name_invalid", message: "name", field: "name" });
  if (!SEMVER.test(d.version)) out.push({ code: "version_invalid", message: "version", field: "version" });
  for (const lang of ["en", "ar"] as const) {
    if (!d.description[lang]?.trim()) out.push({ code: "description_missing", message: `description ${lang}`, field: `description.${lang}`, params: { lang }, severity: lang === "ar" ? "warning" : "error" });
  }
  const included = d.actions.filter((a) => a.include);
  if (!included.length) out.push({ code: "no_actions", message: "no actions" });
  const names = new Set<string>();
  const all = new Set(d.actions.map((a) => a.name));
  for (const a of included) {
    if (!ACTION_NAME.test(a.name)) out.push({ code: "action_name_invalid", message: a.name, action: a.name, field: "name" });
    if (names.has(a.name)) out.push({ code: "action_name_duplicate", message: a.name, action: a.name, field: "name" });
    names.add(a.name);
    for (const lang of ["en", "ar"] as const) {
      if (!a.description[lang]?.trim()) out.push({ code: "description_missing", message: `${a.name} ${lang}`, action: a.name, field: `description.${lang}`, params: { lang }, severity: lang === "ar" ? "warning" : "error" });
    }
    if (a.kind !== "change") continue;
    if (a.kind_locked) {
      out.push({ code: "read_cannot_change", message: a.name, action: a.name, field: "kind" });
      continue;
    }
    if (a.undo?.action) {
      if (a.undo.action === a.name) out.push({ code: "undo_target_self", message: a.name, action: a.name, field: "undo" });
      else if (!all.has(a.undo.action)) out.push({ code: "undo_target_missing", message: a.undo.action, action: a.name, field: "undo", params: { target: a.undo.action } });
      else {
        const target = d.actions.find((x) => x.name === a.undo?.action);
        const req = ((target?.input_schema.required as string[] | undefined) ?? []).filter((p) => !a.undo?.args[p]?.trim());
        if (req.length) out.push({ code: "undo_args_missing", message: req.join(", "), action: a.name, field: "undo", params: { params: req.join(", ") } });
      }
    } else if (!hasUndo(a) && !a.no_undo_reason.trim()) {
      out.push({ code: "undo_or_reason_required", message: a.name, action: a.name, field: "undo" });
    }
  }
  return out;
}

/** Problems that block the next step (warnings never block). */
export function blocking(problems: readonly Problem[]): Problem[] {
  return problems.filter((p) => (p.severity ?? "error") === "error");
}

/** Actions grouped for the review step. */
export function groupActions(d: Draft): { read: DraftAction[]; change: DraftAction[] } {
  return { read: d.actions.filter((a) => a.kind === "read"), change: d.actions.filter((a) => a.kind === "change") };
}

/** Apply every suggestion of one action (kind, undo, PII fields, approvers, always-approve). */
export function applySuggestions(a: DraftAction): DraftAction {
  const s = a.suggestions;
  return {
    ...a,
    kind: a.kind_locked ? a.kind : s.kind.value,
    undo: s.undo ? { action: s.undo.action, args: { ...s.undo.args } } : a.undo,
    pii_fields: [...new Set([...a.pii_fields, ...s.pii.map((p) => p.field)])],
    approver_roles: s.approver_roles ? [...s.approver_roles.value] : a.approver_roles,
    always_approve: s.always_approve ? s.always_approve.value : a.always_approve,
  };
}

/** Bump the minor version: 1.2.3 → 1.3.0 (used when the version already exists). */
export function nextMinor(v: string): string {
  const m = /^(\d+)\.(\d+)\.(\d+)/.exec(v);
  return m ? `${m[1]}.${Number(m[2]) + 1}.0` : "1.0.0";
}

/** Required secret fields that are still empty. */
export function missingSecretFields(kind: AuthKind, values: Record<string, string>): string[] {
  return SECRET_FIELDS[kind].filter((f) => !values[f]?.trim());
}
